/**
 * Identity: the three ways in, the tokens, and the account.
 *
 * EPIC 1. Four stories, and the one that shapes this file most is 1.3 — "I can try the app as a guest
 * and my places survive to sign-up". That is why a guest is a *user row* with `is_guest = true` rather
 * than a client-side sandbox: claiming an account attaches credentials to the existing row, so every
 * place and trip stays where it is. The alternative — creating a second user on sign-up and migrating
 * data — is a migration that can fail halfway for the user who did the most work.
 *
 * Three decisions worth stating, because each is a place a reader would otherwise assume the
 * conventional thing:
 *
 * **The access token is a JWT and the refresh token is opaque.** Not symmetry for its own sake. The
 * access token is presented on every request, so it must be verifiable without a database round trip
 * — that is what "stateless" buys, and why revoking it early is impossible, which is why its lifetime
 * is 15 minutes. The refresh token is presented rarely, so it can afford a lookup, and a lookup is
 * what makes "sign out my lost phone" possible at all.
 *
 * **The access token carries the session id, so logout needs no body.** `sid` is a claim, and
 * `POST /auth/logout` revokes the session it names. That keeps sign-out identical for a browser and a
 * native app even though their refresh tokens are delivered and stored in completely different ways —
 * otherwise the native client would have to send its refresh token back purely to say "this one".
 *
 * **The refresh token's transport differs by client, and the response says which.** A browser gets it
 * as an `httpOnly` cookie it cannot read; a native app gets it in the body and stores it in the OS
 * keychain. Both call the same endpoint. The response carries `refresh_token_transport` so a native
 * client that forgot to identify itself fails immediately and says why, instead of storing nothing and
 * discovering it whenever the access token first expires.
 *
 * **`travel_style` has no fields declared here.** EPIC 6 story 6.1 says a user can state their travel
 * style; the vocabulary is the form's to define and has not been specified. Declaring invented keys
 * would put a fabricated contract in front of a client, so the shape is constrained (a flat record of
 * primitives) and the keys are the server's to store opaquely. See the schema for the exact limit.
 */

import { z } from "zod";

import { Id, IsoDateTime } from "./common.ts";

// -------------------------------------------------------------------------------------- lifetimes

/**
 * Access token lifetime, in seconds. Surfaced in every auth response as `expires_in`.
 *
 * 15 minutes is the contract's number, and the ceiling is real: the token is stateless, so a stolen
 * one works until it expires and nothing can shorten that. A shorter window would mean more refresh
 * round trips, and a refresh is a database write.
 */
export const ACCESS_TOKEN_TTL_SECONDS = 900;

/**
 * Refresh token lifetime. Longer on native, and that asymmetry is deliberate.
 *
 * On the web the cookie is a browser artefact that a shared or lost machine exposes, so 30 days. On
 * native the token lives in the OS keychain behind the device passcode, where a 60-day life is not
 * meaningfully riskier and a 30-day one would sign the user out on an ordinary holiday.
 */
export const REFRESH_TOKEN_TTL_SECONDS = { web: 30 * 24 * 60 * 60, native: 60 * 24 * 60 * 60 } as const;

/** The header a client uses to say which of those two it is. Absent means web. */
export const CLIENT_PLATFORM_HEADER = "x-client-platform";

export const ClientPlatform = z.enum(["web", "android", "ios"]).meta({
  id: "ClientPlatform",
  description: "Sent as `X-Client-Platform`. Absent means `web`, because a browser will not add a custom header without a preflight.",
});

export type ClientPlatform = z.infer<typeof ClientPlatform>;

/**
 * Where the refresh token was put.
 *
 * `cookie` — the response set an `httpOnly` cookie; the value is deliberately not in the body and a
 * page script cannot read it. `body` — the value is in `refresh_token` and the client is responsible
 * for storing it somewhere the OS protects.
 */
export const RefreshTokenTransport = z.enum(["cookie", "body"]).meta({
  id: "RefreshTokenTransport",
  description: "`cookie` for a browser, `body` for a native client. Says where to look rather than leaving it to be inferred.",
});

export type RefreshTokenTransport = z.infer<typeof RefreshTokenTransport>;

// ------------------------------------------------------------------------------------------- user

/**
 * Travel style: explicit, user-stated preferences (EPIC 6 story 6.1).
 *
 * A flat record whose keys this schema does not name, because the vocabulary belongs to the form that
 * will collect it and that form is not specified yet. What is constrained is the *shape*: a nested
 * object would make "show me my stated preferences" a recursive rendering problem, and a top-level
 * array would make it not a set of named preferences at all.
 *
 * `null` is not a value here. "Not stated" is the absence of a key, not a key set to nothing — the one
 * place in this API where that differs from §1.2, and it differs because this is a user-authored map
 * rather than a measured field.
 */
export const TravelStyle = z.record(
  z.string().max(64),
  z.union([z.string().max(200), z.number(), z.boolean(), z.array(z.string().max(200)).max(20)]),
).meta({
  id: "TravelStyle",
  description: "Explicitly stated preferences. Keys are not enumerated: the vocabulary is EPIC 6's to define.",
});

export type TravelStyle = z.infer<typeof TravelStyle>;

/**
 * The account, as the user's own client sees it.
 *
 * `email` is null for a guest, and for a guest that is the whole point of the flag rather than missing
 * data: nothing is unknown, the user has simply not given one yet.
 */
export const AuthUser = z.object({
  id: Id,
  email: z.email().nullable().describe("Null for a guest. Not unknown — simply not provided yet."),
  display_name: z.string(),
  avatar_url: z.string().nullable(),
  is_guest: z.boolean().describe("True until credentials are attached. A guest's data is real and stays through a claim."),
  locale: z.string().describe("BCP 47. `vi-VN` at MVP; no other translation exists yet, and accepting a value does not claim one does."),
  travel_style: TravelStyle,
  created_at: IsoDateTime,
}).meta({ id: "AuthUser", description: "The signed-in account." });

export type AuthUser = z.infer<typeof AuthUser>;

// ------------------------------------------------------------------------------------- signing in

export const PasswordRegisterRequest = z.object({
  email: z.email(),
  /*
   * The policy is enforced in `apps/api/src/auth/password.ts`, not here, and that placement is
   * deliberate: the rules need the email and display name as context (a password containing the
   * user's own address is weak in a way a length check cannot see), and a schema cannot see its
   * siblings. The length bounds here are only a guard against hashing something enormous.
   */
  password: z.string().min(1).max(1024).describe("Policy is checked server-side; see the 400's `errors[]`."),
  display_name: z.string().min(1).max(80),
}).meta({ id: "PasswordRegisterRequest", description: "POST /auth/password/register. Creates a new account." });

export const PasswordLoginRequest = z.object({
  email: z.email(),
  password: z.string().min(1).max(1024),
}).meta({ id: "PasswordLoginRequest", description: "POST /auth/password/login." });

export const GoogleSignInRequest = z.object({
  id_token: z.string().min(1).describe("The ID token from Google Sign-In, verified server-side against Google's JWKS."),
}).meta({ id: "GoogleSignInRequest", description: "POST /auth/google." });

/**
 * `POST /auth/guest/claim` — attach credentials to the guest user, keeping everything it owns.
 *
 * A discriminated union on `method` rather than two optional fields, so a request that supplies both
 * an `id_token` and a `password` is rejected for being contradictory instead of having one of them
 * silently ignored. Which one wins would otherwise be an undocumented detail of the handler.
 */
export const GuestClaimRequest = z.discriminatedUnion("method", [
  z.object({
    method: z.literal("password"),
    email: z.email(),
    password: z.string().min(1).max(1024),
    display_name: z.string().min(1).max(80).optional(),
  }),
  z.object({
    method: z.literal("google"),
    id_token: z.string().min(1),
  }),
]).meta({
  id: "GuestClaimRequest",
  description: "POST /auth/guest/claim. Attaches credentials to the current guest; the user row and everything it owns are kept.",
});

export type GuestClaimRequest = z.infer<typeof GuestClaimRequest>;

/**
 * `POST /auth/refresh`.
 *
 * The body is optional because a browser sends no body at all — its refresh token is in the cookie.
 * A native client sends `refresh_token`. Requiring the field would force the web client to send an
 * empty object for no reason, and making it required-and-nullable would invite a client to send
 * `null` and then wonder why it was signed out.
 */
export const RefreshRequest = z.object({
  refresh_token: z.string().min(1).optional().describe("Required for a native client. A browser sends no body; its token is in the cookie."),
}).meta({ id: "RefreshRequest", description: "POST /auth/refresh. Rotates the refresh token on every call." });

/**
 * What every endpoint that establishes a session returns.
 *
 * Shared by guest, register, login, google, claim and refresh, so a client has one shape to handle
 * rather than five. `expires_in` is seconds rather than an absolute time because the client's clock
 * cannot be trusted to agree with the server's — a countdown from now is correct even when the two
 * disagree, which is the same reason `/meta` exposes `server_time`.
 */
export const AuthSession = z.object({
  access_token: z.string().describe("JWT, HS256, carrying `sub` (user id) and `sid` (session id). Held in memory only; never persisted by a client."),
  expires_in: z.int().positive().describe("Seconds until the access token expires."),
  refresh_token: z.string().nullable().describe("Present only when `refresh_token_transport` is `body`."),
  refresh_token_transport: RefreshTokenTransport,
  refresh_token_expires_in: z.int().positive().describe("Seconds until the refresh token expires."),
  user: AuthUser,
}).meta({ id: "AuthSession", description: "A newly established session, and the account it belongs to." });

export type AuthSession = z.infer<typeof AuthSession>;

// ------------------------------------------------------------------------------------- sessions

/**
 * One signed-in device.
 *
 * `current` marks the session that made this request, so the profile screen can say "this device"
 * instead of making the user guess which row is the phone in their hand. It is computed per request
 * rather than stored — the same row is `current` for one request and not for another, and a stored
 * flag would be wrong for every client but the last one to load the list.
 */
export const SessionInfo = z.object({
  id: Id,
  client: z.string().describe("Free-form description from the client, e.g. `web:Chrome`. Never trusted for authorisation."),
  last_used_at: IsoDateTime.nullable().describe("Null means the session has never been used since it was created."),
  created_at: IsoDateTime,
  current: z.boolean(),
}).meta({ id: "SessionInfo", description: "A signed-in device, for the profile screen." });

export const SessionList = z.object({
  sessions: z.array(SessionInfo),
}).meta({ id: "SessionList", description: "GET /auth/sessions. Every live session; the one making the request is flagged." });

// ---------------------------------------------------------------------------------- the profile

/**
 * `PATCH /me`. Every field optional; an omitted field is left alone.
 *
 * `travel_style` replaces the whole map rather than merging. A merge would make removing a stated
 * preference impossible without a second endpoint, and the map is small enough that sending it whole
 * is not a payload concern.
 */
export const UpdateMeRequest = z.object({
  display_name: z.string().min(1).max(80).optional(),
  locale: z.string().min(2).max(35).optional().describe("BCP 47. `vi-VN` is the only locale with a translation."),
  travel_style: TravelStyle.optional().describe("Replaces the stored map; it is not merged."),
}).meta({ id: "UpdateMeRequest", description: "PATCH /me. Omitted fields are unchanged." });

export type UpdateMeRequest = z.infer<typeof UpdateMeRequest>;

export const MeResponse = z.object({
  user: AuthUser,
}).meta({ id: "MeResponse", description: "GET /me and PATCH /me." });

export type MeResponse = z.infer<typeof MeResponse>;

export type SessionInfo = z.infer<typeof SessionInfo>;
export type SessionList = z.infer<typeof SessionList>;
export type PasswordRegisterRequest = z.infer<typeof PasswordRegisterRequest>;
export type PasswordLoginRequest = z.infer<typeof PasswordLoginRequest>;
export type GoogleSignInRequest = z.infer<typeof GoogleSignInRequest>;
export type RefreshRequest = z.infer<typeof RefreshRequest>;
